/**
 * SpriteRenderingModule
 *
 * Handles D3PlanetSpriteManager setup, deferred sprite queue,
 * planet sprite rendering and position updates.
 * Extracted from ElementRenderingEngine for single-responsibility.
 */

import * as d3 from 'd3';
import { devLog, devWarn, devError } from '../../../../../../utils/devLog';
import { PositionedElement } from '../ElementCalculationEngine';
import {
    D3PlanetSpriteManager,
    D3PlanetAnimator,
    D3CornerHouseAnimator,
    ANIMATION_PRESETS,
    type SpriteUpdateData
} from '../../animation';
import { D3SecondaryDataSpriteManager } from '../../animation/D3SecondaryDataSpriteManager';
import { SECONDARY_DATA_SPRITE_CONFIG, getFormattedPlanetDisplayName } from '../../config/PlanetLayerConfig';
import { DisplayModeService } from '../../../services/DisplayModeService';
import { isRetrogradePlanet } from '../../../DataSections/helpers/planetDisplayHelper';
import { globalEventStream } from '../../core/EventStream';
import type { RenderingConfig, RenderedElement } from '../ElementRenderingEngine';
import { checkPlanetCollision, type PlanetCacheEntry } from './CollisionDetectionModule';

/**
 * Deferred sprite data entry
 */
export interface DeferredSpriteEntry {
    chartId: string;
    containerIndex: number;
    elements: PositionedElement[];
    config: RenderingConfig;
}

/**
 * Callbacks for geometry calculations provided by the main engine
 */
export interface SpriteGeometryCallbacks {
    calculateHousePositionForDegreeLines: (house: number) => { x: number, y: number, width: number, height: number };
    calculatePlanetPositionOld: (containerIndex: number, planetDegree: number, containerBounds: any) => { x: number, y: number } | null;
    calculatePerpendicularPosition: (config: any) => { x: number, y: number } | null;
    calculateConnectionLineRotation: (containerIndex: number, totalDegrees: number, containerBounds: any, linePosition: { x: number, y: number }, perpendicularPosition: { x: number, y: number }) => number;
}

/**
 * Manages all sprite-related operations for the rendering engine
 */
export class SpriteRenderingModule {
    // D3 Sprite Manager for planet animations
    private spriteManager: D3PlanetSpriteManager | null = null;
    private spriteManagers: Map<string, D3PlanetSpriteManager> = new Map();
    private spriteAnimationEnabled: boolean = true;
    private spritesCleared: boolean = false;

    // D3 Secondary Data Sprite Manager for external layer
    private secondaryDataSpriteManager: D3SecondaryDataSpriteManager | null = null;
    private secondaryDataEnabled: boolean = SECONDARY_DATA_SPRITE_CONFIG.ENABLED;

    // Deferred sprite creation after lines.ready
    public deferredSpriteData: DeferredSpriteEntry[] = [];

    // Current chart ID
    private currentChartId?: string;

    // Event subscriptions for cleanup
    private eventSubscriptions: Array<{ unsubscribe: () => void }> = [];

    constructor(private geometry: SpriteGeometryCallbacks) {}

    // --- Chart ID management ---

    public setChartId(chartId?: string): void {
        this.currentChartId = chartId;

        if (chartId) {
            this.spriteManager = this.spriteManagers.get(chartId) || null;

            if (!this.spriteManager) {
                this.initializeD3SpriteSystem();
            }
        }
    }

    public getCurrentChartId(): string | undefined {
        return this.currentChartId;
    }

    // --- Sprite manager access ---

    public getSpriteManager(): D3PlanetSpriteManager | null {
        if (!this.spriteAnimationEnabled) {
            return null;
        }

        // Check if we already have a valid manager for current chart
        if (this.currentChartId && this.spriteManagers.has(this.currentChartId)) {
            const existingManager = this.spriteManagers.get(this.currentChartId);
            if (existingManager && !this.spritesCleared) {
                this.spriteManager = existingManager;
                return this.spriteManager;
            }
        }

        // Force recreation only if sprites were cleared or manager doesn't exist
        if (!this.spriteManager || this.spritesCleared) {
            this.setupD3SpriteManager();
        }

        return this.spriteManager;
    }

    public getSpriteManagerForChart(chartId: string): D3PlanetSpriteManager | undefined {
        return this.spriteManagers.get(chartId);
    }

    public hasSpriteManagerForChart(chartId: string): boolean {
        return this.spriteManagers.has(chartId);
    }

    public isSpriteAnimationEnabled(): boolean {
        return this.spriteAnimationEnabled;
    }

    public setSpriteAnimationEnabled(enabled: boolean): void {
        this.spriteAnimationEnabled = enabled;
    }

    // --- Sprite system initialization ---

    public initializeD3SpriteSystem(): void {
        try {
            const subscription = globalEventStream.subscribe('svg.ready', (event: any) => {
                if (event.chartId === this.currentChartId) {
                    this.setupD3SpriteManager();
                }
            });
            this.eventSubscriptions.push(subscription);
        } catch (_error) {
            this.spriteAnimationEnabled = false;
        }
    }

    public setupD3SpriteManager(): void {
        try {
            // Check if sprite manager already exists for this chart
            if (this.currentChartId) {
                const existingManager = this.spriteManagers.get(this.currentChartId);
                if (existingManager && !this.spritesCleared) {
                    this.spriteManager = existingManager;
                    return;
                }
            }

            // Find chart-specific SVG using chartId
            const svgSelector = this.currentChartId
                ? `svg.chart-svg-${this.currentChartId}`
                : 'svg.astrology-chart-svg';
            const svg = document.querySelector(svgSelector) as SVGSVGElement;
            if (!svg) {
                return;
            }

            const d3svg = d3.select(svg);

            // Dispose old manager if it exists
            if (this.spriteManager) {
                this.spriteManager = null;
            }

            this.spriteManager = new D3PlanetSpriteManager(d3svg, this.currentChartId);

            // Store in chart-specific map
            if (this.currentChartId) {
                this.spriteManagers.set(this.currentChartId, this.spriteManager);
            }

            // Add to window for testing
            window.d3PlanetSpriteManager = this.spriteManager;
            this.spritesCleared = false;

        } catch (_error) {
            this.spriteAnimationEnabled = false;
        }
    }

    // --- Secondary Data Sprite System ---

    public initializeSecondaryDataSpriteSystem(): void {
        try {
            if (typeof window !== 'undefined' && this.secondaryDataEnabled) {
                this.setupSecondaryDataSpriteManager();
            }
        } catch (_error) {
            this.secondaryDataEnabled = false;
        }
    }

    private setupSecondaryDataSpriteManager(): void {
        if (this.secondaryDataSpriteManager) {
            return;
        }

        try {
            const svg = document.querySelector('svg.astrology-chart-svg') as SVGSVGElement;
            if (!svg) {
                return;
            }

            const d3svg = d3.select(svg);
            this.secondaryDataSpriteManager = new D3SecondaryDataSpriteManager(d3svg);
        } catch (_error) {
            this.secondaryDataEnabled = false;
        }
    }

    public getSecondaryDataSpriteManager(): D3SecondaryDataSpriteManager | null {
        return this.secondaryDataSpriteManager;
    }

    public isSecondaryDataEnabled(): boolean {
        return this.secondaryDataEnabled;
    }

    // --- Chart clear handling ---

    public handleChartDataClear(event: any, chartId?: string): void {
        try {
            devLog(`[SpriteRenderingModule] handleChartDataClear called for chartId: ${chartId || 'all'}`);

            const targetSpriteManager = chartId
                ? this.spriteManagers.get(chartId)
                : this.spriteManager;

            if (targetSpriteManager) {
                devLog(`[SpriteRenderingModule] Clearing sprites for ${chartId || 'current'}`);
                targetSpriteManager.clearAllSprites();
                if (!chartId || chartId === this.currentChartId) {
                    this.spritesCleared = true;
                }
                devLog('[SpriteRenderingModule] Sprites cleared');
            } else {
                devLog('[SpriteRenderingModule] No sprite manager available');
                this.spritesCleared = false;
            }

            // Clear degree lines and related elements from DOM - scoped to specific chart if chartId provided
            const overlaySelector = chartId
                ? `#lines-overlay-${chartId}, #planets-overlay-${chartId}`
                : '.lines-overlay, .planets-overlay';
            const allOverlays = d3.selectAll(overlaySelector);

            devLog(`[SpriteRenderingModule] Found ${allOverlays.size()} overlay containers for ${chartId || 'all'}`);

            if (!allOverlays.empty()) {
                const circleCount = allOverlays.selectAll('.planet-circle-on-line').size();
                const textCount = allOverlays.selectAll('.planet-text-on-line').size();
                devLog(`[SpriteRenderingModule] Elements to clear: circles=${circleCount}, texts=${textCount}`);

                allOverlays.selectAll('.planet-circle-on-line').remove();
                allOverlays.selectAll('.planet-text-on-line').remove();
                allOverlays.selectAll('[data-element-id^="planet-circle-house-"]').remove();
                allOverlays.selectAll('[data-element-id^="planet-connection-house-"]').remove();
                allOverlays.selectAll('[data-element-id^="planet-text-house-"]').remove();
                allOverlays.selectAll('.planet-element').remove();
                allOverlays.selectAll('.yoga-element').remove();

                devLog('[SpriteRenderingModule] DOM elements cleared');
            } else {
                devLog('[SpriteRenderingModule] No overlay containers found!');
            }

        } catch (error) {
            devError('SpriteRenderingModule: Error clearing chart elements:', error);
        }
    }

    public clearSpritesForRerender(): void {
        devLog('[SpriteRenderingModule] clearSpritesForRerender called');

        if (this.spriteManager) {
            devLog('[SpriteRenderingModule] Clearing sprites for rerender');
            this.spriteManager.clearAllSprites();
            this.spritesCleared = true;
        }
    }

    // --- Deferred sprite processing ---

    public processDeferredSprites(globalPlanetCache: PlanetCacheEntry[]): void {
        if (this.deferredSpriteData.length === 0) {
            return;
        }

        // If sprites were cleared, we need to force sprite manager recreation
        if (this.spritesCleared) {
            this.spriteManager = null;
            this.spritesCleared = false;
        }

        const spriteManager = this.getSpriteManager();
        if (!spriteManager) {
            devWarn('SpriteRenderingModule: Cannot process deferred sprites - sprite manager not available');
            return;
        }

        // Process each deferred container
        this.deferredSpriteData.forEach(({ containerIndex, elements, config }) => {
            try {
                this.renderPlanetSprites(containerIndex, elements, spriteManager, config, performance.now(), globalPlanetCache);
            } catch (_error) {
                // Sprite creation error handled silently
            }
        });

        // Reset sprite state - sprites are now successfully created
        this.spritesCleared = false;

        // Clear deferred data
        this.deferredSpriteData = [];
    }

    /**
     * Defer sprite creation for a container until lines.ready fires
     */
    public deferSpriteCreation(containerIndex: number, degreeLineElements: PositionedElement[], config: RenderingConfig): void {
        if (!this.spriteAnimationEnabled || degreeLineElements.length === 0) {
            return;
        }

        const chartId = config.chartId || 'default-chart';
        const existingIndex = this.deferredSpriteData.findIndex(item =>
            item.containerIndex === containerIndex && item.chartId === chartId
        );

        if (existingIndex === -1) {
            this.deferredSpriteData.push({
                chartId,
                containerIndex,
                elements: [...degreeLineElements],
                config: { ...config }
            });
        }
    }

    // --- Planet sprite rendering ---

    /**
     * Calculate planet sprite position using the same logic as renderPlanetSprites
     * DRY: Single source of truth for planet positioning calculations
     */
    public calculatePlanetSpritePosition(params: {
        containerIndex: number;
        degreesInRashi: number;
        globalDegrees: number;
        planetName: string;
        positionIndex: number;
    }, globalPlanetCache: PlanetCacheEntry[]): { position: { x: number; y: number }; rotation: number } | null {
        const { containerIndex, degreesInRashi, globalDegrees, planetName, positionIndex } = params;

        const housePosition = this.geometry.calculateHousePositionForDegreeLines(containerIndex);
        const containerBounds = {
            width: housePosition.width,
            height: housePosition.height,
            x: 0,
            y: 0
        };

        const linePosition = this.geometry.calculatePlanetPositionOld(containerIndex, degreesInRashi, containerBounds);
        if (!linePosition) {
            return null;
        }

        const perpendicularPosition = this.geometry.calculatePerpendicularPosition({
            containerIndex,
            containerBounds,
            linePosition,
            yogaDegree: degreesInRashi,
            positionIndex,
            planetName,
            useCollisionDetection: true,
            globalDegree: globalDegrees
        });

        if (!perpendicularPosition) {
            return null;
        }

        // Adjust for absolute positioning
        perpendicularPosition.x += housePosition.x;
        perpendicularPosition.y += housePosition.y;

        // Adjust linePosition for rotation calculation
        const absoluteLinePosition = {
            x: linePosition.x + housePosition.x,
            y: linePosition.y + housePosition.y
        };

        const rotation = this.geometry.calculateConnectionLineRotation(
            containerIndex,
            globalDegrees,
            containerBounds,
            absoluteLinePosition,
            perpendicularPosition
        );

        return { position: perpendicularPosition, rotation };
    }

    /**
     * Modern D3 Sprite-based planet rendering
     */
    public renderPlanetSprites(
        containerIndex: number,
        degreeLineElements: PositionedElement[],
        spriteManager: D3PlanetSpriteManager,
        config: RenderingConfig,
        startTime: number,
        globalPlanetCache: PlanetCacheEntry[]
    ): RenderedElement[] {
        const renderedElements: RenderedElement[] = [];

        // Clear planet position cache for collision detection when starting a new container
        if (containerIndex === 0) {
            globalPlanetCache.length = 0;
        }

        // Find planets that belong to this container
        const housePlanets = degreeLineElements.filter(element =>
            element.containerIndex === containerIndex &&
            element.position &&
            element.type !== 'yoga'
        );

        if (housePlanets.length === 0) {
            return renderedElements;
        }

        // Sort planets by degree for proper positioning
        housePlanets.sort((a, b) => {
            const degreeA = a.position?.position?.degrees || 0;
            const degreeB = b.position?.position?.degrees || 0;
            return degreeA - degreeB;
        });

        // Create or update sprites for each planet
        housePlanets.forEach((planet, index) => {
            if (!planet.position?.position) {
                return;
            }

            const planetFullName = planet.name;
            const planetName = planet.position.graha;

            let totalDegrees: number;
            let localDegreeInHouse: number;

            if (planet.position?.positionString) {
                const parts = planet.position.positionString.split('|');
                if (parts.length >= 3) {
                    totalDegrees = parseFloat(parts[2]) || 0;
                    localDegreeInHouse = parseFloat(parts[1]) || 0;
                } else {
                    totalDegrees = planet.position.position.degrees +
                        (planet.position.position.minutes || 0) / 60 +
                        (planet.position.position.seconds || 0) / 3600;
                    localDegreeInHouse = totalDegrees % 30;
                }
            } else if (planet.degree !== undefined) {
                totalDegrees = planet.degree;
                localDegreeInHouse = totalDegrees % 30;
            } else {
                totalDegrees = planet.position.position.degrees +
                    (planet.position.position.minutes || 0) / 60 +
                    (planet.position.position.seconds || 0) / 3600;
                localDegreeInHouse = totalDegrees % 30;
            }

            const positionResult = this.calculatePlanetSpritePosition({
                containerIndex,
                degreesInRashi: localDegreeInHouse,
                globalDegrees: totalDegrees,
                planetName,
                positionIndex: index
            }, globalPlanetCache);

            if (!positionResult) {
                return;
            }

            const { position: perpendicularPosition, rotation } = positionResult;

            if (isNaN(perpendicularPosition.x) || isNaN(perpendicularPosition.y)) {
                devWarn(`[SpriteRenderingModule] Invalid coordinates for ${planetName}:`, perpendicularPosition);
                return;
            }

            let sprite = spriteManager.getSprite(planetName);
            if (!sprite) {
                const horaCurrentPlanet = config.horaCurrentPlanet;

                sprite = spriteManager.createPlanetSprite(
                    planetName,
                    containerIndex,
                    perpendicularPosition,
                    {},
                    { name: planetFullName, shortName: planetName },
                    horaCurrentPlanet
                );

                D3PlanetAnimator.stopAndResetSprite(sprite, perpendicularPosition, rotation);

            } else {
                if (config.enableAnimations) {
                    D3PlanetAnimator.animatePlanetSprite(
                        sprite,
                        perpendicularPosition,
                        rotation,
                        ANIMATION_PRESETS.FAST
                    ).catch(error => {
                        devWarn(`[D3 SPRITES] Animation failed for ${planetName}:`, error);
                    });
                } else {
                    D3PlanetAnimator.stopAndResetSprite(sprite, perpendicularPosition, rotation);
                }
            }

            // Handle retrograde display
            if (planet.position?.isRetrograde) {
                const currentMode = DisplayModeService.getDisplayMode(config.chartId);
                const displayName = getFormattedPlanetDisplayName(planetName, currentMode, true);
                sprite.text.text(displayName);
            }

            renderedElements.push({
                id: `sprite-${planetName}-house-${containerIndex}`,
                element: sprite.container.node() as SVGElement,
                type: 'sprite',
                layer: 'lines-overlay',
                debugInfo: {
                    renderTime: performance.now() - startTime,
                    elementCount: 3,
                    memoryEstimate: 150
                }
            });
        });

        return renderedElements;
    }

    // --- Planet position updates from drag/motion ---

    public updatePlanetPositionsFromDrag(
        allPlanets: { planets: Record<string, { longitude: number; rashiIndex: number; degreesInRashi: number; speed?: number } | null>; timestamp: Date },
        globalPlanetCache: PlanetCacheEntry[],
        targetSpriteManager?: D3PlanetSpriteManager
    ): void {
        const spriteManager = targetSpriteManager || this.spriteManager;
        if (!spriteManager) return;

        // Clear collision cache for fresh calculation
        globalPlanetCache.length = 0;

        const planetEntries = Object.entries(allPlanets.planets)
            .filter(([_, data]) => data !== null) as [string, { longitude: number; rashiIndex: number; degreesInRashi: number; speed?: number }][];

        planetEntries.sort((a, b) => a[1].longitude - b[1].longitude);

        const updates: SpriteUpdateData[] = [];

        planetEntries.forEach(([planetKey, planetData], index) => {
            const containerIndex = (planetData.rashiIndex + 1) % 12;

            const result = this.calculatePlanetSpritePosition({
                containerIndex,
                degreesInRashi: planetData.degreesInRashi,
                globalDegrees: planetData.longitude,
                planetName: planetKey,
                positionIndex: index
            }, globalPlanetCache);

            if (!result) return;

            const isRetrograde = isRetrogradePlanet(planetKey, planetData.speed);

            updates.push({
                planetName: planetKey,
                position: result.position,
                rotation: result.rotation,
                isRetrograde
            });
        });

        if (updates.length > 0) {
            spriteManager.updateAllSprites(updates, ANIMATION_PRESETS.FAST);
        }
    }

    // --- Motion buffer update ---

    public updatePlanetPositionsFromMotionBuffer(
        motionData: any,
        enableAnimations: boolean,
        animationDuration: number,
        globalPlanetCache: PlanetCacheEntry[]
    ): Promise<void> {
        const spriteManager = this.getSpriteManager();
        if (!spriteManager || !this.spriteAnimationEnabled) {
            devWarn('[MOTION BUFFER] D3 sprite system not available, skipping animation update');
            return Promise.resolve();
        }

        try {
            const spriteUpdates: any[] = [];

            Object.entries(motionData).forEach(([_planetName, planetData]: [string, any]) => {
                if (planetData && typeof planetData === 'object' && !('error' in planetData)) {
                    const sprite = spriteManager.getSprite(_planetName);
                    if (!sprite) {
                        return;
                    }

                    const longitude = planetData.longitude || 0;
                    const containerIndex = sprite.containerIndex;

                    const housePosition = this.geometry.calculateHousePositionForDegreeLines(containerIndex);
                    const containerBounds = {
                        width: housePosition.width,
                        height: housePosition.height,
                        x: 0,
                        y: 0
                    };

                    const totalDegrees = longitude % 30;

                    const linePosition = this.geometry.calculatePlanetPositionOld(containerIndex, totalDegrees, containerBounds);
                    if (!linePosition) {
                        return;
                    }

                    linePosition.x += housePosition.x;
                    linePosition.y += housePosition.y;

                    const localDegreeInHouse = totalDegrees % 30;
                    const perpendicularPosition = this.geometry.calculatePerpendicularPosition({
                        containerIndex,
                        containerBounds,
                        linePosition,
                        yogaDegree: localDegreeInHouse,
                        positionIndex: 0,
                        useCollisionDetection: false
                    });
                    if (!perpendicularPosition) {
                        return;
                    }

                    let rotation = 0;
                    if (D3CornerHouseAnimator.isCornerHouse(containerIndex)) {
                        rotation = D3CornerHouseAnimator.calculateCornerRotation(containerIndex, longitude);
                    }

                    spriteUpdates.push({
                        sprite,
                        position: perpendicularPosition,
                        rotation
                    });
                }
            });

            if (spriteUpdates.length > 0) {
                const animationConfig = {
                    duration: animationDuration,
                    easing: enableAnimations ? ANIMATION_PRESETS.SMOOTH.easing : ANIMATION_PRESETS.FAST.easing
                };

                return D3PlanetAnimator.updateMultipleSprites(spriteUpdates, animationConfig).then(() => {});
            } else {
                return Promise.resolve();
            }

        } catch (error) {
            devError('[MOTION BUFFER] Failed to update planet positions:', error);
            return Promise.reject(error);
        }
    }

    // --- Sprite stats ---

    public getSpriteSystemStats(): any {
        const spriteManager = this.getSpriteManager();
        if (!spriteManager) {
            return {
                enabled: false,
                error: 'Sprite manager not available'
            };
        }

        return {
            enabled: this.spriteAnimationEnabled,
            stats: spriteManager.getSpriteStats()
        };
    }

    // --- Cleanup ---

    public dispose(): void {
        try {
            // Unsubscribe from all event subscriptions
            this.eventSubscriptions.forEach(sub => sub.unsubscribe());
            this.eventSubscriptions = [];

            if (this.spriteManager) {
                this.spriteManager.dispose();
                this.spriteManager = null;
            }

            if (this.secondaryDataSpriteManager) {
                this.secondaryDataSpriteManager.dispose();
                this.secondaryDataSpriteManager = null;
            }

            this.spriteManagers.forEach((manager, _chartId) => {
                try {
                    if (typeof manager.dispose === 'function') {
                        manager.dispose();
                    }
                } catch (_error) {
                    // Silently handle errors
                }
            });
            this.spriteManagers.clear();

            this.deferredSpriteData.length = 0;

        } catch (error) {
            devError('SpriteRenderingModule: Error during disposal:', error);
        }
    }
}
